import { create } from 'zustand';
import { isAxiosError } from 'axios';
import { useCases } from '../../infrastructure/di/container';
import { getErrorMessage } from '../../infrastructure/http/common';
import { ChannelImportDraftEntity, ChannelImportReport, ChannelImportRow } from '../../domain/entities/ChannelImportDraft';
import { TelegramAccountEntity } from '../../domain/entities/TelegramAccount';

const ACCOUNTS_PAGE_SIZE = 100;

const toErrorSlug = (e: unknown): string => {
  if (isAxiosError(e)) return getErrorMessage(e);
  if (e instanceof Error && e.message) return e.message;
  return 'unknown-error';
};

interface ChannelImportState {
  draft?: ChannelImportDraftEntity;
  rows: ChannelImportRow[];
  isDirty: boolean;
  preview?: ChannelImportReport;
  accounts: TelegramAccountEntity[];
  recent: ChannelImportDraftEntity[];
  isLoading: boolean;
  isSaving: boolean;
  error?: string;
  loadAccounts: () => Promise<void>;
  loadRecent: () => Promise<void>;
  start: (rawLinks: string, telegramAccountUuid: string) => Promise<string>;
  open: (uuid: string) => Promise<void>;
  refresh: () => Promise<void>;
  setRows: (rows: ChannelImportRow[]) => void;
  runPreview: () => Promise<void>;
  apply: () => Promise<void>;
  reset: () => void;
}

const applyDraft = (draft: ChannelImportDraftEntity) => ({ draft, rows: draft.rows, isDirty: false });

export const useChannelImportStore = create<ChannelImportState>((set, get) => {
  const saveRowsIfDirty = async () => {
    const { draft, rows, isDirty } = get();
    if (!draft || !isDirty) return;
    const saved = await useCases.saveChannelImportRows().execute(draft.uuid, rows);
    set(applyDraft(saved));
  };

  const withSaving = async <T>(action: () => Promise<T>): Promise<T> => {
    set({ isSaving: true, error: undefined });
    try {
      return await action();
    } catch (e) {
      const slug = toErrorSlug(e);
      set({ error: slug });
      throw new Error(slug);
    } finally {
      set({ isSaving: false });
    }
  };

  return {
    rows: [],
    isDirty: false,
    accounts: [],
    recent: [],
    isLoading: false,
    isSaving: false,
    async loadAccounts() {
      try {
        const result = await useCases.listTelegramAccounts().execute({ page: 1, perPage: ACCOUNTS_PAGE_SIZE, search: '' });
        set({ accounts: result.items.filter((account) => account.status === 'AUTHENTICATED') });
      } catch (e) {
        set({ error: toErrorSlug(e) });
      }
    },
    async loadRecent() {
      try {
        set({ recent: await useCases.listChannelImports().execute() });
      } catch (e) {
        set({ error: toErrorSlug(e) });
      }
    },
    start(rawLinks, telegramAccountUuid) {
      return withSaving(async () => {
        const draft = await useCases.startChannelImport().execute(rawLinks, telegramAccountUuid);
        set({ ...applyDraft(draft), preview: undefined });
        return draft.uuid;
      });
    },
    async open(uuid) {
      if (get().draft?.uuid === uuid) return;
      set({ isLoading: true, error: undefined, preview: undefined });
      try {
        set({ ...applyDraft(await useCases.getChannelImport().execute(uuid)), isLoading: false });
      } catch (e) {
        set({ isLoading: false, error: toErrorSlug(e) });
      }
    },
    async refresh() {
      const { draft, isDirty } = get();
      if (!draft) return;
      try {
        const fresh = await useCases.getChannelImport().execute(draft.uuid);
        set(isDirty ? { draft: fresh } : applyDraft(fresh));
      } catch (e) {
        set({ error: toErrorSlug(e) });
      }
    },
    setRows(rows) {
      set({ rows, isDirty: true, preview: undefined });
    },
    runPreview() {
      return withSaving(async () => {
        await saveRowsIfDirty();
        const { draft } = get();
        if (!draft) return;
        set({ preview: await useCases.applyChannelImport().execute(draft.uuid, true) });
      });
    },
    apply() {
      return withSaving(async () => {
        await saveRowsIfDirty();
        const { draft } = get();
        if (!draft) return;
        await useCases.applyChannelImport().execute(draft.uuid, false);
        set({ ...applyDraft(await useCases.getChannelImport().execute(draft.uuid)), preview: undefined });
      });
    },
    reset() {
      set({ draft: undefined, rows: [], isDirty: false, preview: undefined, error: undefined });
    },
  };
});
